/**
 * Polls conversation status to detect remotely-running agent tasks.
 *
 * When a user refreshes or reconnects to a conversation that is still
 * MESSAGE_IN_PROGRESS on the backend (running on another server replica),
 * this hook detects it and exposes state so the UI can show the cancel
 * button and reload history when the run finishes.
 *
 * Polling is only active when the conversation is MESSAGE_IN_PROGRESS
 * or MESSAGE_CANCELLED (backend still winding down) and no local stream
 * is running.
 *
 * After a local cancel, call `refetchStatus()` to re-fetch the backend
 * status so the poll kicks in and waits for the backend to finish.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { AssistantApi } from "~/components/assistant/api/AssistantApi";
import type { ConversationStatus, ConversationStatusResponse } from "~/components/assistant/types";

const POLL_INTERVAL_MS = 3000;

interface UseConversationStatusOptions {
  api: AssistantApi;
  conversationId: string | null;
  /** True when the local AG-UI runtime has an active stream */
  isLocalRunActive: boolean;
}

interface UseConversationStatusReturn {
  /** The conversation is running on a remote replica (not this browser tab) */
  isRunningRemotely: boolean;
  /** Bumped each time a remote run transitions from in-progress → finished */
  remoteRunFinishedCount: number;
  /** Re-fetch backend status (use after local cancel to start polling). */
  refetchStatus: () => Promise<ConversationStatusResponse | null>;
}

export function useConversationStatus({
  api,
  conversationId,
  isLocalRunActive,
}: UseConversationStatusOptions): UseConversationStatusReturn {
  const [remoteStatus, setRemoteStatus] = useState<ConversationStatus | null>(null);
  const [remoteRunFinishedCount, setRemoteRunFinishedCount] = useState(0);
  const prevRemotelyRunning = useRef(false);

  // Fetch status once on conversation load / change
  useEffect(() => {
    setRemoteStatus(null);
    prevRemotelyRunning.current = false;

    if (!conversationId) {
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const resp = await api.getConversationStatus(conversationId);
        if (!cancelled) {
          setRemoteStatus(resp.status ?? "READY");
        }
      } catch (error) {
        console.error("Failed to fetch conversation status:", error);
        if (!cancelled) {
          setRemoteStatus("READY");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, conversationId]);

  // Remotely running when no local stream is active and backend hasn't settled.
  // MESSAGE_CANCELLED is transient — the backend is still winding down and hasn't
  // persisted the final messages yet, so keep polling until READY.
  const isRunningRemotely =
    !isLocalRunActive &&
    (remoteStatus === "MESSAGE_IN_PROGRESS" || remoteStatus === "MESSAGE_CANCELLED");

  // Poll while remotely running
  useEffect(() => {
    if (!conversationId || !isRunningRemotely) {
      return;
    }

    const timer = setInterval(async () => {
      try {
        const resp = await api.getConversationStatus(conversationId);
        setRemoteStatus(resp.status ?? "READY");
      } catch (error) {
        console.error("Failed to poll conversation status:", error);
      }
    }, POLL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [api, conversationId, isRunningRemotely]);

  // Detect transition from remotely-running → finished
  useEffect(() => {
    if (prevRemotelyRunning.current && !isRunningRemotely) {
      setRemoteRunFinishedCount((c) => c + 1);
    }
    prevRemotelyRunning.current = isRunningRemotely;
  }, [isRunningRemotely]);

  // Re-fetch backend status on demand (e.g. after local cancel to start polling)
  const refetchStatus = useCallback(async () => {
    if (!conversationId) return null;
    try {
      const resp = await api.getConversationStatus(conversationId);
      setRemoteStatus(resp.status ?? "READY");
      return resp;
    } catch (error) {
      console.error("Failed to refetch conversation status:", error);
      return null;
    }
  }, [api, conversationId]);

  return {
    isRunningRemotely,
    remoteRunFinishedCount,
    refetchStatus,
  };
}
